// Monri success/cancel return URLs, built and parsed in one place.
//
// Monri appends its own return params (acquirer, amount, approval_code, ...,
// enrollment, order_number, response_code, digest) to success_url, and
// language + order_number to cancel_url. Verified on ipgtest (2026-10-23):
// `?` when the URL has no query, `&` when it has one. Its params can still
// collide with ours (Monri always sends `enrollment=Y|N`, the old group URL
// used `enrollment=<id>`), and the official plugins guard against a second `?`
// as well. So our identifiers go in the PATH and the query is left entirely to
// Monri. The old query-based form is still parsed for payment forms opened
// before the switch (`legacy*` below), tolerating both separators.
//
// Pure: imported by the API handlers that build the URLs and by the result
// pages that parse them.

export type PaymentOutcome = "success" | "cancelled";
/** "unknown" renders the neutral "processing" state on the result pages. */
export type ReturnOutcome = PaymentOutcome | "unknown";

// Sessions paid by card and hold tokens are always crypto.randomUUID().
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// An enrollment retry reuses the client's existing row, whose id may predate
// the uuid generator (seed rows such as "gte-alpha-1"), so the charset is checked.
const ENROLLMENT_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
// Subscription pay tokens are 48 hex chars today; accept the URL-safe charset
// so a token minted by an older generator still opens its page.
const SUBSCRIPTION_TOKEN_RE = /^[A-Za-z0-9_-]{1,128}$/;

export function isReturnUuid(value: string): boolean {
  return UUID_RE.test(value);
}

export function isEnrollmentReturnId(value: string): boolean {
  return ENROLLMENT_ID_RE.test(value);
}

export function isSubscriptionPayToken(value: string): boolean {
  return SUBSCRIPTION_TOKEN_RE.test(value);
}

/**
 * One of OUR values from a legacy return URL, with whatever Monri glued onto it
 * cut off at the first `?`. A repeated key (Monri's own `enrollment` collides
 * with ours) arrives as an array; ours is always the first occurrence.
 */
export function cutMonriSuffix(value: unknown): string {
  const first = Array.isArray(value) ? value[0] : value;
  if (typeof first !== "string" && typeof first !== "number") return "";
  return String(first).split("?")[0]!.trim();
}

export function parseOutcome(value: unknown): ReturnOutcome {
  const v = cutMonriSuffix(value);
  if (v === "success") return "success";
  // "cancel" is what the old subscription cancel_url carried.
  if (v === "cancelled" || v === "cancel") return "cancelled";
  return "unknown";
}

export interface SessionReturn {
  outcome: ReturnOutcome;
  /** "" when missing or not a uuid. */
  sessionId: string;
  /** Finalize-link token of a therapist-created hold; "" when absent or invalid. */
  holdToken: string;
}

/** An id that does not validate also drops the outcome to "unknown". */
export function parseSessionReturn(input: { outcome: unknown; session: unknown; token?: unknown }): SessionReturn {
  const sessionId = cutMonriSuffix(input.session);
  if (!isReturnUuid(sessionId)) return { outcome: "unknown", sessionId: "", holdToken: "" };
  const token = cutMonriSuffix(input.token);
  return { outcome: parseOutcome(input.outcome), sessionId, holdToken: isReturnUuid(token) ? token : "" };
}

/** Old `/booking/payment-result?status=..&session=..&token=..`. */
export function legacySessionSearch(search: Record<string, unknown>): SessionReturn {
  return parseSessionReturn({ outcome: search.status, session: search.session, token: search.token });
}

export interface EnrollmentReturn {
  outcome: ReturnOutcome;
  /** "" when missing or outside the id charset. */
  enrollmentId: string;
}

export function parseEnrollmentReturn(input: { outcome: unknown; enrollment: unknown }): EnrollmentReturn {
  const enrollmentId = cutMonriSuffix(input.enrollment);
  if (!isEnrollmentReturnId(enrollmentId)) return { outcome: "unknown", enrollmentId: "" };
  return { outcome: parseOutcome(input.outcome), enrollmentId };
}

/** Old `/grupne-terapije/payment-result?status=..&enrollment=..`. */
export function legacyEnrollmentSearch(search: Record<string, unknown>): EnrollmentReturn {
  return parseEnrollmentReturn({ outcome: search.status, enrollment: search.enrollment });
}

export interface ReturnUrls {
  success_url: string;
  cancel_url: string;
}

function bothOutcomes(build: (outcome: PaymentOutcome) => string): ReturnUrls {
  return { success_url: build("success"), cancel_url: build("cancelled") };
}

/** `/booking/payment-result/<outcome>/<sessionId>[/<holdToken>]` */
export function sessionReturnUrls(appUrl: string, sessionId: string, holdToken?: string | null): ReturnUrls {
  const tail = holdToken ? `/${encodeURIComponent(holdToken)}` : "";
  return bothOutcomes(
    (o) => `${appUrl}/booking/payment-result/${o}/${encodeURIComponent(sessionId)}${tail}`,
  );
}

/** `/grupne-terapije/payment-result/<outcome>/<enrollmentId>` */
export function enrollmentReturnUrls(appUrl: string, enrollmentId: string): ReturnUrls {
  return bothOutcomes(
    (o) => `${appUrl}/grupne-terapije/payment-result/${o}/${encodeURIComponent(enrollmentId)}`,
  );
}

/** `/subscription/pay/<token>/<outcome>` */
export function subscriptionReturnUrls(appUrl: string, token: string): ReturnUrls {
  return bothOutcomes((o) => `${appUrl}/subscription/pay/${encodeURIComponent(token)}/${o}`);
}
